export const WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE = 'workspace-context-upload' as const;
export const PAGE_CHANGE_SET_STATUS_ITEM_TYPE = 'page-change-set' as const;

export type WorkspaceContextUploadStatus = 'queued' | 'processing' | 'ready' | 'failed';
export type PageChangeSetStatus = 'staged' | 'applying' | 'completed' | 'partly_applied' | 'canceled' | 'failed';
export type ChatStatusItemType =
    | typeof WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE
    | typeof PAGE_CHANGE_SET_STATUS_ITEM_TYPE;

export type ChatStatusItemBase<TType extends ChatStatusItemType, TStatus extends string> = {
    id: string;
    type: TType;
    status: TStatus;
    updatedAt: number;
};

export type ChatStatusItemEventBase<TType extends ChatStatusItemType> = {
    id: string;
    type: TType;
};

export type WorkspaceContextUploadStatusItem = ChatStatusItemBase<
    typeof WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
    WorkspaceContextUploadStatus
> & {
    contextFileId: string;
    filename: string;
    title: string;
    userVisibleError: string;
    userVisibleNotice: string;
};

export type WorkspaceContextUploadStatusItemEvent = (
    | WorkspaceContextUploadStatusItemUpsertEvent
    | WorkspaceContextUploadStatusItemRemoveEvent
);

type WorkspaceContextUploadStatusItemUpsertEvent = ChatStatusItemEventBase<
    typeof WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE
> & {
    contextFileId?: string;
    filename?: string;
    title?: string;
    status: WorkspaceContextUploadStatus;
    userVisibleError?: string;
    userVisibleNotice?: string;
    removed?: false;
};

type WorkspaceContextUploadStatusItemRemoveEvent = ChatStatusItemEventBase<
    typeof WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE
> & {
    contextFileId?: string;
    removed: true;
};

/** One entry of an addition's content: an option, a question with its options, a column or a phase. */
export type PageChangeContentItem = {
    text: string;
    detail: string;
    correct: boolean;
    items: PageChangeContentItem[];
};

/** Where a staged addition lands: a new row of the set, or the empty container at the end of the page. */
export type PageChangePlacement = {
    row: number;
    column: number;
    twoColumn: boolean;
    containerId: string | null;
};

export type PageChangeOperation = {
    id: string;
    op: string;
    widgetType: string;
    icon: string;
    title: string;
    status: string;
    reason: string | null;
    /** The widget it added or changed, once applied. */
    widgetId: string | null;
    /** An addition's full description as plain text. */
    description: string;
    items: PageChangeContentItem[];
    /** Title of the widget an added word cloud, summary or mind map reads. */
    source: string | null;
    placement: PageChangePlacement | null;
    /** The widget an edit changes. */
    target: string | null;
    /** An edit's new values: `title` as plain text, `text` as cleaned HTML. */
    fields: Record<string, string>;
};

/** Changes staged on one page, waiting for the user to apply or cancel them, or the outcome once they did. */
export type PageChangeSetStatusItem = ChatStatusItemBase<
    typeof PAGE_CHANGE_SET_STATUS_ITEM_TYPE,
    PageChangeSetStatus
> & {
    revision: number;
    pageId: string;
    pageTitle: string;
    operations: PageChangeOperation[];
};

export type PageChangeSetStatusItemEvent = ChatStatusItemEventBase<typeof PAGE_CHANGE_SET_STATUS_ITEM_TYPE> & {
    status: PageChangeSetStatus;
    revision: number;
    pageId: string;
    pageTitle: string;
    operations: PageChangeOperation[];
    removed?: false;
};

export type ChatStatusItem =
    | WorkspaceContextUploadStatusItem
    | PageChangeSetStatusItem;

export type ChatStatusItemEvent =
    | WorkspaceContextUploadStatusItemEvent
    | PageChangeSetStatusItemEvent;

export function chatStatusItemEventFromUnknown(value: unknown): ChatStatusItemEvent | null {
    if (!isRecord(value)) {
        return null;
    }

    switch (value.type) {
        case WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE:
            return workspaceContextUploadStatusItemEventFromRecord(value);
        case PAGE_CHANGE_SET_STATUS_ITEM_TYPE:
            return pageChangeSetStatusItemEventFromRecord(value);
        default:
            return null;
    }
}

export function chatStatusItemFromEvent(event: ChatStatusItemEvent, now: number): ChatStatusItem | null {
    if (chatStatusItemEventRemovesItem(event)) {
        return null;
    }

    switch (event.type) {
        case WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE:
            return workspaceContextUploadStatusItemFromEvent(event, now);
        case PAGE_CHANGE_SET_STATUS_ITEM_TYPE:
            return {...event, updatedAt: now};
        default:
            return null;
    }
}

export function chatStatusItemIdFromEvent(event: ChatStatusItemEvent): string {
    switch (event.type) {
        case WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE:
            return stringValue(event.id) || stringValue(event.contextFileId);
        case PAGE_CHANGE_SET_STATUS_ITEM_TYPE:
            return stringValue(event.id);
        default:
            return '';
    }
}

export function chatStatusItemEventRemovesItem(
    event: ChatStatusItemEvent,
): event is Extract<ChatStatusItemEvent, {removed: true}> {
    return event.removed === true;
}

export function chatStatusItemsEqual(left: ChatStatusItem, right: ChatStatusItem): boolean {
    return left.id === right.id
        && left.type === right.type
        && left.status === right.status
        && statusItemSpecificFingerprint(left) === statusItemSpecificFingerprint(right);
}

export function isWorkspaceContextUploadStatusItem(
    item: ChatStatusItem,
): item is WorkspaceContextUploadStatusItem {
    return item.type === WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE;
}

const PAGE_CHANGE_SET_STATUS_ORDER: Record<PageChangeSetStatus, number> = {
    staged: 0,
    applying: 1,
    completed: 2,
    partly_applied: 2,
    failed: 2,
    canceled: 2,
};

/**
 * Whether an incoming item is older than the one already shown. A change set only moves forward, to a higher
 * revision or on from staged, while the HTTP answer and the websocket push for it can arrive in either order.
 */
export function isOutdatedStatusItem(existing: ChatStatusItem, incoming: ChatStatusItem): boolean {
    if (!isPageChangeSetStatusItem(existing) || !isPageChangeSetStatusItem(incoming)) {
        return false;
    }

    return incoming.revision < existing.revision
        || (incoming.revision === existing.revision
            && PAGE_CHANGE_SET_STATUS_ORDER[incoming.status] < PAGE_CHANGE_SET_STATUS_ORDER[existing.status]);
}

export function isPageChangeSetStatusItem(item: ChatStatusItem): item is PageChangeSetStatusItem {
    return item.type === PAGE_CHANGE_SET_STATUS_ITEM_TYPE;
}

export function workspaceContextUploadStatus(value: unknown): WorkspaceContextUploadStatus | null {
    return value === 'queued' || value === 'processing' || value === 'ready' || value === 'failed'
        ? value
        : null;
}

function workspaceContextUploadStatusItemEventFromRecord(
    record: Record<string, unknown>,
): WorkspaceContextUploadStatusItemEvent | null {
    if (record.removed === true) {
        return {
            type: WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
            id: stringValue(record.id),
            contextFileId: stringValue(record.contextFileId),
            removed: true,
        };
    }

    const status = workspaceContextUploadStatus(record.status);
    if (status === null) {
        return null;
    }

    return {
        type: WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
        id: stringValue(record.id),
        contextFileId: stringValue(record.contextFileId),
        filename: stringValue(record.filename),
        title: stringValue(record.title),
        status,
        userVisibleError: stringValue(record.userVisibleError),
        userVisibleNotice: stringValue(record.userVisibleNotice),
    };
}

function pageChangeSetStatusItemEventFromRecord(record: Record<string, unknown>): PageChangeSetStatusItemEvent | null {
    const status = record.status;
    if (
        status !== 'staged' && status !== 'applying' && status !== 'completed'
        && status !== 'partly_applied' && status !== 'canceled' && status !== 'failed'
    ) {
        return null;
    }

    return {
        type: PAGE_CHANGE_SET_STATUS_ITEM_TYPE,
        id: stringValue(record.id),
        status,
        revision: typeof record.revision === 'number' ? record.revision : 0,
        pageId: stringValue(record.pageId),
        pageTitle: stringValue(record.pageTitle),
        operations: Array.isArray(record.operations)
            ? record.operations.filter(isRecord).map(pageChangeOperationFromRecord)
            : [],
    };
}

function pageChangeOperationFromRecord(operation: Record<string, unknown>): PageChangeOperation {
    const placement = isRecord(operation.placement) ? operation.placement : null;
    const fields: Record<string, string> = {};
    if (isRecord(operation.fields)) {
        Object.entries(operation.fields).forEach(([name, value]) => {
            if (typeof value === 'string') {
                fields[name] = value;
            }
        });
    }

    return {
        id: stringValue(operation.id),
        op: stringValue(operation.op),
        widgetType: stringValue(operation.widgetType),
        icon: stringValue(operation.icon),
        title: stringValue(operation.title),
        status: stringValue(operation.status),
        reason: stringValue(operation.reason) || null,
        widgetId: stringValue(operation.widgetId) || null,
        description: stringValue(operation.description),
        items: contentItemsFrom(operation.items),
        source: stringValue(operation.source) || null,
        placement: placement === null
            ? null
            : {
                row: typeof placement.row === 'number' ? placement.row : 0,
                column: typeof placement.column === 'number' ? placement.column : 0,
                twoColumn: placement.twoColumn === true,
                containerId: stringValue(placement.containerId) || null,
            },
        target: stringValue(operation.target) || null,
        fields,
    };
}

function contentItemsFrom(value: unknown): PageChangeContentItem[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter(isRecord).map(item => ({
        text: stringValue(item.text),
        detail: stringValue(item.detail),
        correct: item.correct === true,
        items: contentItemsFrom(item.items),
    })).filter(item => item.text !== '');
}

function workspaceContextUploadStatusItemFromEvent(
    event: WorkspaceContextUploadStatusItemUpsertEvent,
    now: number,
): WorkspaceContextUploadStatusItem | null {
    const id = chatStatusItemIdFromEvent(event);
    const status = workspaceContextUploadStatus(event.status);
    if (id === '' || status === null) {
        return null;
    }

    return {
        id,
        type: WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
        contextFileId: stringValue(event.contextFileId) || id,
        status,
        filename: stringValue(event.filename),
        title: stringValue(event.title),
        userVisibleError: stringValue(event.userVisibleError),
        userVisibleNotice: stringValue(event.userVisibleNotice),
        updatedAt: now,
    };
}

function statusItemSpecificFingerprint(item: ChatStatusItem): string {
    switch (item.type) {
        case WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE:
            return [
                item.contextFileId,
                item.filename,
                item.title,
                item.userVisibleError,
                item.userVisibleNotice,
            ].join('\u0001');
        case PAGE_CHANGE_SET_STATUS_ITEM_TYPE:
            return [item.revision, item.pageTitle, JSON.stringify(item.operations)].join('\u0001');
        default:
            return '';
    }
}

function stringValue(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
